import {
  forwardRef,
  useState,
  useRef,
  useEffect,
  type SelectHTMLAttributes,
  type ReactNode,
} from 'react'
import { cn } from '@lib/utils'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  icon?: ReactNode
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> {
  /** Select label */
  label?: string
  /** Helper text below select */
  helperText?: string
  /** Error message */
  error?: string
  /** Select options */
  options: SelectOption[]
  /** Placeholder text */
  placeholder?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Full width */
  fullWidth?: boolean
  /** Change handler */
  onChange?: (value: string) => void
}

/**
 * Select dropdown component with brutalist styling
 *
 * @example
 * <Select
 *   label="Country"
 *   options={[{ value: 'us', label: 'United States' }]}
 *   onChange={setValue}
 * />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      helperText,
      error,
      options,
      placeholder = 'Select an option',
      size = 'md',
      fullWidth = false,
      disabled,
      value,
      onChange,
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).slice(2, 9)}`
    const hasError = !!error

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={selectId}
            className="text-body-sm font-medium text-text-primary"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className={cn(
              // Base styles
              'w-full appearance-none bg-surface text-text-primary',
              'border transition-all cursor-pointer',
              'focus:outline-none',

              // Size variants
              size === 'sm' && 'h-8 px-3 pr-8 text-body-sm',
              size === 'md' && 'h-10 px-4 pr-10 text-body',
              size === 'lg' && 'h-12 px-4 pr-12 text-body-lg',

              // States
              !hasError && [
                'border-border',
                'hover:border-border-strong',
                'focus:border-primary focus:shadow-brutalist',
              ],
              hasError && [
                'border-error',
                'focus:border-error focus:shadow-brutalist-error',
              ],
              disabled && [
                'bg-surface-elevated border-border-subtle',
                'text-text-muted cursor-not-allowed',
              ],

              // Placeholder state
              !value && 'text-text-muted',

              className
            )}
            aria-invalid={hasError}
            {...props}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>

          {/* Dropdown arrow */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {(error || helperText) && (
          <p
            className={cn(
              'text-caption',
              error ? 'text-error' : 'text-text-muted'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

/**
 * Custom select with dropdown panel (for more control)
 */
export interface CustomSelectProps {
  label?: string
  options: SelectOption[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  disabled?: boolean
  error?: string
  searchable?: boolean
}

export function CustomSelect({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  size = 'md',
  fullWidth = false,
  disabled = false,
  error,
  searchable = false,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find((opt) => opt.value === value)

  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen, searchable])

  const hasError = !!error

  return (
    <div
      ref={containerRef}
      className={cn('relative flex flex-col gap-1.5', fullWidth && 'w-full')}
    >
      {label && (
        <label className="text-body-sm font-medium text-text-primary">
          {label}
        </label>
      )}

      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          // Base styles
          'w-full flex items-center justify-between bg-surface text-left',
          'border transition-all',
          'focus:outline-none',

          // Size variants
          size === 'sm' && 'h-8 px-3 text-body-sm',
          size === 'md' && 'h-10 px-4 text-body',
          size === 'lg' && 'h-12 px-4 text-body-lg',

          // States
          !hasError && [
            'border-border',
            'hover:border-border-strong',
            isOpen && 'border-primary shadow-brutalist',
          ],
          hasError && 'border-error',
          disabled && [
            'bg-surface-elevated border-border-subtle',
            'text-text-muted cursor-not-allowed',
          ],

          // Selected vs placeholder
          selectedOption ? 'text-text-primary' : 'text-text-muted'
        )}
      >
        <span className="truncate">
          {selectedOption?.icon && (
            <span className="mr-2">{selectedOption.icon}</span>
          )}
          {selectedOption?.label || placeholder}
        </span>
        <svg
          className={cn(
            'shrink-0 ml-2 transition-transform',
            isOpen && 'rotate-180'
          )}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-dropdown mt-1 bg-surface border border-border shadow-brutalist animate-fade-in-down">
          {/* Search input */}
          {searchable && (
            <div className="p-2 border-b border-border">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-2 bg-surface-elevated text-text-primary text-body-sm border border-border focus:border-primary focus:outline-none"
              />
            </div>
          )}

          {/* Options list */}
          <div className="max-h-60 overflow-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-body-sm text-text-muted">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  onClick={() => {
                    onChange?.(option.value)
                    setIsOpen(false)
                    setSearchTerm('')
                  }}
                  className={cn(
                    'w-full flex items-center px-4 py-2 text-left transition-colors',
                    size === 'sm' && 'text-body-sm',
                    size === 'md' && 'text-body',
                    size === 'lg' && 'text-body-lg',
                    option.value === value
                      ? 'bg-primary-muted text-primary'
                      : 'text-text-primary hover:bg-surface-elevated',
                    option.disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {option.icon && <span className="mr-2">{option.icon}</span>}
                  <span className="truncate">{option.label}</span>
                  {option.value === value && (
                    <svg
                      className="ml-auto shrink-0"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {error && <p className="text-caption text-error">{error}</p>}
    </div>
  )
}

export default Select
